'use client';

import { forwardRef, type ComponentPropsWithoutRef, type ComponentRef } from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';

import { cn } from '@r/utils/utils.ts';
import { cva, type VariantProps } from 'class-variance-authority';

const baseStyles = {
  base: [
    'peer',
    'inline-flex',
    'shrink-0',
    'text-foreground',
    'cursor-pointer',
    'items-center',
    'rounded-full',
    'border-2',
    'border-transparent',
    'transition-colors'
  ],
  state: ['data-[state=checked]:bg-primary', 'data-[state=unchecked]:bg-muted'],
  focus: [
    'focus-visible:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-ring',
    'focus-visible:ring-offset-2',
    'focus-visible:ring-offset-background'
  ],
  disabled: ['disabled:cursor-not-allowed', 'disabled:opacity-30']
};

const switchVariants = cva(
  [...baseStyles.base, ...baseStyles.state, ...baseStyles.focus, ...baseStyles.disabled].join(' '),
  {
    variants: {
      switchSize: {
        default: 'h-6 w-11',
        sm: 'h-5 w-9',
        lg: 'h-7 w-[3.25rem]'
      }
    },
    defaultVariants: {
      switchSize: 'default'
    }
  }
);

const switchThumbVariants = cva(
  'pointer-events-none block rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=unchecked]:translate-x-0',
  {
    variants: {
      switchThumbSize: {
        default: 'h-5 w-5 data-[state=checked]:translate-x-5',
        sm: 'h-4 w-4 data-[state=checked]:translate-x-4',
        lg: 'h-6 w-6 data-[state=checked]:translate-x-6'
      }
    },
    defaultVariants: {
      switchThumbSize: 'default'
    }
  }
);

export interface SwitchProps
  extends ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
    ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
    VariantProps<typeof switchVariants> {
  switchSize?: 'default' | 'sm' | 'lg';
}

const Switch = forwardRef<ComponentRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, switchSize, ...props }, ref) => (
    <SwitchPrimitives.Root
      className={cn(switchVariants({ switchSize, className }))}
      {...props}
      ref={ref}
    >
      <SwitchPrimitives.Thumb
        className={cn(switchThumbVariants({ className, switchThumbSize: switchSize }))}
      />
    </SwitchPrimitives.Root>
  )
);
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
